"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.doSimplex = doSimplex;
exports.gjkIntersect = gjkIntersect;
exports.supportMesh = supportMesh;
exports.supportPoly = supportPoly;
exports.transformSupportFunction = transformSupportFunction;
var _vector = require("./vector");
// Inspiration taken from https://www.youtube.com/watch?v=ajv46BSqcK4 and https://www.youtube.com/watch?v=Qupqu1xe7Io

function logVec(v, name) {
  console.log(`${name}: (${v.x}, ${v.y}, ${v.z})`);
}
function logSimplex(simplex, name) {
  let s = `${name}:\n`;
  simplex.forEach((v, i) => {
    s += `  ${i}: (${v.x}, ${v.y}, ${v.z})\n`;
  });
  console.log(s);
}

/**
 * Returns the support point for a convex polygon in the direction of the given vector. Uses steepest ascent, beginning
 * with startIndex if provided, or the first vertex otherwise.
 * @param poly A convex polygon
 * @param direction The direction to find the support point in
 * @param startIndex The index of the first point to check
 * @returns 
 */
function supportPoly(poly, direction, startIndex = 0) {
  let maxIndex = startIndex !== null && startIndex !== void 0 ? startIndex : 0;
  let searchDirection = 1;
  let triedBothDirections = false;
  let maxDot = (0, _vector.dot_3)(poly[maxIndex], direction);
  while (true) {
    let nextIndex = (maxIndex + searchDirection + poly.length) % poly.length;
    let nextDot = (0, _vector.dot_3)(poly[nextIndex], direction);
    if (nextDot > maxDot) {
      maxIndex = nextIndex;
      maxDot = nextDot;
    } else if (triedBothDirections) {
      break;
    } else {
      searchDirection *= -1;
      triedBothDirections = true;
    }
  }
  return poly[maxIndex];
}

/**
 * Returns the support vertex for a convex connected mesh in the direction of the given vector. Uses steepest ascent,
 * beginning with startIndex if provided, or the first vertex otherwise.
 * @param mesh A connected mesh
 * @param direction The direction to find the support point in
 * @param startIndex The index of the first point to check
 * @returns The support vertex as a ConnectedVertex
 */
function supportMesh(mesh, direction, startIndex = 0) {
  let currentIndex = startIndex !== null && startIndex !== void 0 ? startIndex : 0;
  let current = mesh.verts[currentIndex];
  let maxDot = (0, _vector.dot_3)(current.p, direction);
  while (true) {
    let maxNeighborIndex = -1;
    let maxNeighborDot = -Infinity;
    for (let i = 0; i < current.e.length; i++) {
      for (let j = 0; j < mesh.edges[current.e[i]].v.length; j++) {
        let neighborIndex = mesh.edges[current.e[i]].v[j];
        let neighborDot = (0, _vector.dot_3)(mesh.verts[neighborIndex].p, direction);
        if (neighborDot > maxNeighborDot) {
          maxNeighborIndex = neighborIndex;
          maxNeighborDot = neighborDot;
        }
      }
    }
    if (maxNeighborDot <= maxDot) {
      break;
    }
    currentIndex = maxNeighborIndex;
    current = mesh.verts[currentIndex];
    maxDot = maxNeighborDot;
  }
  return current;
}

/**
 * Given a support function, returns a new support function that acts as if the support points were transformed by the
 * given matrix. The matrix must be a rigid body transformation (i.e. no scaling).
 * @param supportFunction A support function
 * @param mat The 4x4 matrix to transform the support points by
 * @param iRot The inverse of the rotation component of mat. It is used to transform the supportFunction direction. If
 * not provided, it will be calculated.
 * @returns A new support function that transforms the support points by the given matrix
 * 
 * @remarks It does not actually transform the support points. Rather, it applies the inverse of the rotation component
 * of the matrix to the support function direction. After supportFunction returns, it applies the full matrix to the
 * support point.
 */
function transformSupportFunction(supportFunction, mat, iRot) {
  if (!iRot) {
    iRot = (0, _vector.invertMatrix3)((0, _vector.getMatrix3FromMatrix4)(mat));
  }
  const ciRot = iRot;
  return direction => {
    // let transformedDirection = multiplyMatrix3Point(ciRot, direction)
    // let support = supportFunction(transformedDirection)
    // return multiplyMatrix4Point(mat, support)
    return (0, _vector.multiplyMatrix4Point)(mat, supportFunction((0, _vector.multiplyMatrix3Point)(ciRot, direction)));
  };
}

/**
 * Determines if two convex shapes intersect.
 * @param supportA A function that returns the support point of the first object in a given direction
 * @param supportB A function that returns the support point of the second object in a given direction
 * @param dim The dimension of the shapes. Accepted values are 2 and 3. Default is 3.
 * @param initialDirection The initial direction to search. Default is (1, 0, 0).
 * @returns true if the two objects intersect, false otherwise
 * 
 * @remarks
 * Convex shapes A and B are each defined by a "support function" that returns the point on the shape that is farthest
 * in a given direction. In other words, the point for which `dot_3(point, direction)` is a maximum.
 * 
 * An interesting property of convex shapes is that the two shapes intersect if and only if their Minkowski difference
 * contains the origin. The Minkowski difference is represented by the following expression, which is the difference of
 * A's support point in the given direction, and B's support point in the opposite direction:
 *
 * ```
 *   sub_3(
 *     supportA(direction),
 *     supportB(mul_3(direction, -1))
 *   )
 * ```
 * 
 * The goal of this function is to find four points (a "simplex") on the Minkowski difference that surround the
 * origin. (Or three points, for 2D shapes.) If a simplex can be found which surrounds the origin, then the two shapes
 * intersect.
 * 
 * This is done by initializing a simplex with a support point in the initial direction. We then update the direction to
 * be orthogonal to the current simplex in the direction of the origin. (If the simplex is a point, the direction is
 * directly toward the origin.) We find the support point in that direction and add it to the simplex. The worst points
 * are removed from the simplex as we go. This process of choosing a new direction and updating the simplex with
 * successively closer points is repeated until we have surrounded the origin, and we return true. However, if a new
 * point does not move us "beyond" the origin, this means the points cannot surround the origin, so we return false.
 * 
 * The algorithm used here is the GJK algorithm. Here are some good videos that explain it:
 * - https://www.youtube.com/watch?v=ajv46BSqcK4
 * - https://www.youtube.com/watch?v=Qupqu1xe7Io
 * 
 * 
 */
function gjkIntersect(supportA, supportB, dim = 3, initialDirection = {
  x: 1,
  y: 0,
  z: 0
}) {
  if (dim !== 2 && dim !== 3) {
    throw new Error('Invalid dimension. Must be 2 or 3.');
  }
  let direction = initialDirection;
  // Begin with a simplex containing just a single support point in the initial direction
  const simplex = [(0, _vector.sub_3)(supportA(direction), supportB((0, _vector.mul_3)(direction, -1)))];

  // Set new direction to be from this support point toward the origin
  direction = (0, _vector.mul_3)(simplex[0], -1); // direction = ORIGIN - simplex[0]
  // logSimplex(simplex, 'Initial simplex')
  // logVec(direction, 'Initial direction')
  let iterations = 0;
  while (true) {
    iterations++;
    if (iterations > 100) {
      console.log('Too many iterations.');
      throw new Error(`Too many iterations.`);
    }
    // Add the support point in the new direction to the simplex, as long as it moves us "beyond" the origin
    let newPoint = (0, _vector.sub_3)(supportA(direction), supportB((0, _vector.mul_3)(direction, -1)));
    // logVec(direction, 'direction')
    // logVec(newPoint, 'newPoint')
    let dotCheck = (0, _vector.dot_3)(newPoint, direction);
    // console.log('Checking if new point moved us beyond the origin')
    // console.log('dotCheck = ' + dotCheck)
    if (dotCheck <= 0) {
      // The new point did not move us "beyond" the origin. This means that we cannot surround the origin, so the shapes
      // do not intersect.
      // console.log('New point did not move us beyond the origin. Shapes do not intersect.')
      return false;
    }
    simplex.push(newPoint);

    // logSimplex(simplex, 'It did, so adding point to simplex')

    // Determine if the simplex surrounds the origin. If not, update the direction and continue.
    if (doSimplex(simplex, direction, dim)) {
      // The simplex surrounds the origin, so the shapes intersect.
      return true;
    }
  }
}

/**
 * Given a simplex and a direction, determines if the simplex contains the origin. If it does not, the direction is
 * updated.
 * @param simplex The simplex
 * @param direction The direction
 * @param dim The dimension of the simplex. Accepted values are 2 and 3. Default is 3.
 * @returns true if the simplex contains the origin, false otherwise. If false, the simplex and direction are updated.
 */
function doSimplex(simplex, direction, dim = 3) {
  // logSimplex(simplex, 'doSimplex simplex')
  // logVec(direction, 'doSimplex direction')
  switch (simplex.length) {
    case 2:
      {
        const [B, A] = simplex;
        /*
         * A is the newest point:
         *
         *          3
         *
         * ---------A---------
         *          |
         *    2 <---|---> 1
         *          |
         * ---------B---------
         *
         * Because of the check performed in the loop in gjkIntersect, we know that A has "passed" the origin, so the origin cannot be
         * in region 3. So the origin must be in either region 1 or 2. Choose the direction that will move us closer to the origin.
         */

        // and the new simplex will be a triangle. 
        const AO = (0, _vector.mul_3)(A, -1);
        const AB = (0, _vector.sub_3)(B, A);
        let newDirection = (0, _vector.cross_3)((0, _vector.cross_3)(AB, AO), AB);
        // logVec(newDirection, 'newDirection')
        direction.x = newDirection.x;
        direction.y = newDirection.y;
        direction.z = newDirection.z;
        return false;
      }
    case 3:
      {
        /*
         * A is the newest point:
         *
         *          /
         *    7    /
         *        /  4   /
         * ------B      /
         *       | \   /   
         *       |  \ /
         *    3  | 1 A    5
         *       |  / \  
         *       | /   \ 
         * ------C      \
         *        \  2   \     
         *    6    \
         *          \  
         * 
         * In the last iteration, we previously chose A in the direction of the origin, so we know that the origin cannot be
         * in region 3.
         * 
         * Similarly, we previously checked that the origin is "between" B and C, so we know that it cannot be in regions
         * 6 or 7.
         * 
         * We have also just barely checked that A has moved "beyond" the origin, so we know that the origin cannot be in
         * region 5.
         * 
         * That leaves regions 1, 2, and 4. We can determine which by checking which side of AB and AC the origin is on.
         * 
         * If dim === 2, then if the origin is in region 1, we are finished at that point. Otherwise, we return a
         * direction normal to the triangle toward the origin.
         */

        const [C, B, A] = simplex;
        const AO = (0, _vector.mul_3)(A, -1);
        const AB = (0, _vector.sub_3)(B, A);
        const AC = (0, _vector.sub_3)(C, A);
        const ABC = (0, _vector.cross_3)(AB, AC); // Normal to triangle ABC
        const ABnormal = (0, _vector.cross_3)(AB, ABC); // Normal to edge AB, pointing away from the triangle
        if ((0, _vector.dot_3)(ABnormal, AO) > 0) {
          // The origin is outside of the triangle's AB edge (region 4). C will be removed from the simplex, and the new
          // direction will be ABnormal. EDIT: This is wrong in 3D. The new direction should be AB x AO x AB, not AB x
          // ABC. Reference: https://youtu.be/Qupqu1xe7Io?t=1999
          let newDirection = (0, _vector.cross_3)((0, _vector.cross_3)(AB, AO), AB);
          simplex.splice(0, 1); // [B, A]
          direction.x = newDirection.x;
          direction.y = newDirection.y;
          direction.z = newDirection.z;
          return false;
        }
        const ACnormal = (0, _vector.cross_3)(ABC, AC); // Normal to edge AC, pointing away from the triangle
        if ((0, _vector.dot_3)(ACnormal, AO) > 0) {
          // The origin is outside of the triangle's AC edge (region 2). B will be removed from the simplex, and the new
          // direction will be ACnormal. EDIT: This is wrong in 3D. The new direction should be AC x AO x AC, not AC x
          // ABC. Reference: https://youtu.be/Qupqu1xe7Io?t=1999
          let newDirection = (0, _vector.cross_3)((0, _vector.cross_3)(AC, AO), AC);
          simplex.splice(1, 1); // [C, A]
          direction.x = newDirection.x;
          direction.y = newDirection.y;
          direction.z = newDirection.z;
          return false;
        }

        // The origin must be above or below the triangle (region 1).

        if (dim === 3) {
          // Choose a direction normal to the triangle toward the origin. So, O - A must be in the same direction as ABC,
          // or dot(AO, ABC) > 0. Otherwise, we need to flip the direction.
          if ((0, _vector.dot_3)(AO, ABC) > 0) {
            direction.x = ABC.x;
            direction.y = ABC.y;
            direction.z = ABC.z;
          } else {
            direction.x = -ABC.x;
            direction.y = -ABC.y;
            direction.z = -ABC.z;
            // Swap B and C in the simplex so the winding direction is predictable in the next step.
            simplex[0] = B;
            simplex[1] = C;
          }
          return false;
        } else {
          // The origin is in region 1, so the shapes intersect.
          return true;
        }
      }
    case 4:
      {
        if (dim === 2) throw new Error('Invalid simplex length for 2D: 4');

        /*
         * A is the newest point:
         *
         * Side view:             
         *               2       
         * 
         *       --------A--------
         *              /|\
         *             / | \
         *            /  |  \
         *           /   |   \
         *          / _.-D-._ \
         * --------B'^-------^'C--------
         * 
         *               1
         * 
         * Top down:
         * 
         *        \  8  /
         *         \   /
         *          \ /   
         *           D
         *     5    /|\    4
         *         / | \ 
         *  \     /  |  \     / 
         *   \   /  .A.  \   /
         *    \ / .'   '. \ /
         *     B-'-------'-C
         * 6   |           |    7
         *     |     3     |
         *     |           |
         * 
         *
         * In the last iteration, we previously chose A in the direction of the origin, so we know that the origin cannot
         * be below triangle BCD in region 1. This means the origin must be directly above triangle BCD. (This does not necessarily
         * mean that A is above BCD, though, as it appears in the top-down view.)
         * 
         * We also previously checked that A is "beyond" the origin, which means the origin cannot be in region 2.
         * 
         * The origin is therefore in one of the following regions:
         * 
         *  0 Inside simplex ABCD
         *  3 Closest to triangle ABC
         *  4 Closest to triangle ACD
         *  5 Closest to triangle ADB
         *  6 Closest to edge AB
         *  7 Closest to edge AC
         *  8 Closest to edge AD
         * 
         * It cannot be closest to A only, because it is not beyond A, and even if A is not directly above BCD, the origin
         * is, and so the origin will always be closer to an edge or face than it will be to A.
         * 
         * It also cannot be closest to B or C or D, for similar reasons.
         * 
         * First, check to see if the origin is "below" triangles ABC, ACD, and ADB. If so, then it is in the simplex, and
         * we are done.
         * 
         * If it is not, then for each of the three triangular prisms extending out from ABC, ACD, and ADB, (regions 3, 4,
         * and 5 above) check the top two planes of each until we find one where the origin is "under" both. If we find
         * one, then the origin is closest to that triangle, so set the direction to be normal to that triangle, and
         * continue.
         * 
         * If we don't find one, then we need to find a pair of adjacent planes of those prisms where the origin is
         * "above" both, as well as "below" ABC, ACD, or ADB on the opposite side. (regions 6, 7, and 8).
         * 
         * We can potentially use knowledge from the first check (is it inside the simplex) to determine which pairs don't
         * need to be checked.
         * 
         * It's also possible we could use information about previous iterations. For instance, we know B is the most
         * recently added point (C and D might have neen swapped for correct winding, so we can't be sure about them) so
         * that could rule out regions 4 for instance, but perhaps not. We might just have to see empirically if any
         * branches are never taken.
         */

        const [D, C, B, A] = simplex;

        // Check if the origin is "below" the triangles ABC, ACD, and ADB
        const AO = (0, _vector.mul_3)(A, -1); // Vector from A to the origin
        const AB = (0, _vector.sub_3)(B, A); // Vector from A to B
        const AC = (0, _vector.sub_3)(C, A); // Vector from A to C
        const AD = (0, _vector.sub_3)(D, A); // Vector from A to D
        const ABC = (0, _vector.cross_3)(AB, AC); // Normal to triangle ABC, pointing out of the simplex
        const ACD = (0, _vector.cross_3)(AC, AD); // Normal to triangle ACD, pointing out of the simplex
        const ADB = (0, _vector.cross_3)(AD, AB); // Normal to triangle ADB, pointing out of the simplex
        const belowABC = (0, _vector.dot_3)(AO, ABC) <= 0;
        const belowACD = (0, _vector.dot_3)(AO, ACD) <= 0;
        const belowADB = (0, _vector.dot_3)(AO, ADB) <= 0;
        if (belowABC && belowACD && belowADB) {
          // The origin is inside the simplex, so we are done.
          // console.log('origin is inside simplex ABCD')
          return true;
        }
        let ABnormal_to_ABC = (0, _vector.cross_3)(AB, ABC); // Normal to edge AB, pointing away from triangle ABC
        let ACnormal_to_ABC = (0, _vector.cross_3)(ABC, AC); // Normal to edge AC, pointing away from triangle ABC
        let belowAB_normal_to_ABC = (0, _vector.dot_3)(AO, ABnormal_to_ABC) <= 0;
        let belowAC_normal_to_ABC = (0, _vector.dot_3)(AO, ACnormal_to_ABC) <= 0;
        let ACnormal_to_ACD = (0, _vector.cross_3)(AC, ACD); // Normal to edge AC, pointing away from triangle ACD
        let ADnormal_to_ACD = (0, _vector.cross_3)(ACD, AD); // Normal to edge AD, pointing away from triangle ACD
        let belowAC_normal_to_ACD = (0, _vector.dot_3)(AO, ACnormal_to_ACD) <= 0;
        let belowAD_normal_to_ACD = (0, _vector.dot_3)(AO, ADnormal_to_ACD) <= 0;
        let ADnormal_to_ADB = (0, _vector.cross_3)(AD, ADB); // Normal to edge AD, pointing away from triangle ADB
        let ABnormal_to_ADB = (0, _vector.cross_3)(ADB, AB); // Normal to edge AB, pointing away from triangle ADB
        let belowAD_normal_to_ADB = (0, _vector.dot_3)(AO, ADnormal_to_ADB) <= 0;
        let belowAB_normal_to_ADB = (0, _vector.dot_3)(AO, ABnormal_to_ADB) <= 0;

        // Check to see if the origin is directly above ABC, ACD, or ADB

        // Check to see if the origin is directly above ABC
        if (!belowABC && belowAB_normal_to_ABC && belowAC_normal_to_ABC) {
          // The origin is inside trianglular prism ABC, so we are done.
          // console.log('origin is inside triangular prism ABC')
          simplex.splice(0, 1); // [C, B, A]
          direction.x = ABC.x;
          direction.y = ABC.y;
          direction.z = ABC.z;
          return false;
        }

        // Check to see if the origin is directly above ACD
        if (!belowACD && belowAC_normal_to_ACD && belowAD_normal_to_ACD) {
          // The origin is inside triangular prism ACD, so we are done.
          // console.log('origin is inside triangular prism ACD')
          simplex.splice(2, 1); // [D, C, A]
          direction.x = ACD.x;
          direction.y = ACD.y;
          direction.z = ACD.z;
          return false;
        }

        // Check to see if the origin is directly above ADB
        if (!belowADB && belowAD_normal_to_ADB && belowAB_normal_to_ADB) {
          // The origin is inside triangular prism ADB, so we are done.
          // console.log('origin is inside triangular prism ADB')
          simplex.splice(1, 1); // [D, B, A]
          // Swap B and D so that the winding is correct
          simplex[0] = B; // [B, B, A]
          simplex[1] = D; // [B, D, A]
          direction.x = ADB.x;
          direction.y = ADB.y;
          direction.z = ADB.z;
          return false;
        }

        // The origin is not inside the simplex, and it is not directly above ABC, ACD, or ADB, so it must be above one of
        // the edges of the simplex. In order for the origin to be above an edge, it must be outside the triangular prisms
        // on two adjacent sides of the edge.

        // Check edge AB
        if (!belowAB_normal_to_ABC && !belowAB_normal_to_ADB) {
          // The origin is above edge AB, so we are done.
          // console.log('origin is above edge AB')
          simplex.splice(0, 2); // [B, A]
          let newDirection = (0, _vector.cross_3)((0, _vector.cross_3)(AB, AO), AB);
          direction.x = newDirection.x;
          direction.y = newDirection.y;
          direction.z = newDirection.z;
          return false;
        }

        // Check edge AC
        if (!belowAC_normal_to_ABC && !belowAC_normal_to_ACD) {
          // The origin is above edge AC, so we are done.
          // console.log('origin is above edge AC')
          simplex.splice(2, 1); // [D, C, A]
          simplex.splice(0, 1); // [C, A]
          let newDirection = (0, _vector.cross_3)((0, _vector.cross_3)(AC, AO), AC);
          direction.x = newDirection.x;
          direction.y = newDirection.y;
          direction.z = newDirection.z;
          return false;
        }

        // Check edge AD
        if (!belowAD_normal_to_ACD && !belowAD_normal_to_ADB) {
          // The origin is above edge AD, so we are done.
          // console.log('origin is above edge AD')
          simplex.splice(1, 2); // [D, A]
          let newDirection = (0, _vector.cross_3)((0, _vector.cross_3)(AD, AO), AD);
          direction.x = newDirection.x;
          direction.y = newDirection.y;
          direction.z = newDirection.z;
          return false;
        }
        console.log('Could not determine which region the origin is in!');
        logSimplex(simplex, 'simplex');
        logVec(direction, 'direction');
        throw new Error('Could not determine which region the origin is in!');
      }
    default:
      throw new Error(`Invalid simplex length: ${simplex.length}`);
  }
}